/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * UI v2 theme: flat surfaces, 1px borders, 8px radius, Red Hat Text (Display for headings),
 * brand blue for every primary action. Built from
 * design-reference/enterprise-shell/pearl-enterprise-theme.js.
 *
 * It EXTENDS assets/theme rather than replacing it, so MDBox, MDButton,
 * MDTypography and friends keep finding the template's custom keys
 * (palette.gradients, boxShadows.colored, borders.borderRadius.section,
 * functions.*). Those keys are kept but flattened: gradients get
 * start === end, coloured shadows become "none", and the 999px "section"
 * radius becomes 8px.
 *
 * The legacy component overrides bake their radii and heights in at import
 * time, so every one that conflicts with v2 density is overridden here
 * explicitly rather than assumed to follow `borders`.
 *
 * No body zoom: v2 renders at 100% and sizes type and density instead
 * (14px base, 36px controls, 48px table rows).
 */

import { createTheme } from "@mui/material/styles";
import baseTheme from "assets/theme";
import pxToRem from "assets/theme/functions/pxToRem";
import { APP_SCALE_MIN_WIDTH } from "assets/theme/base/appScale";
import { V2_DISPLAY_FONT_FAMILY, V2_FONT_FAMILY, v2 as t, v2Density as d, v2Tones } from "styles/tokens/v2";

const font = V2_FONT_FAMILY;
const tabular = { fontVariantNumeric: "lining-nums tabular-nums" };

const flatGradients = Object.fromEntries(
  Object.entries(baseTheme.palette.gradients || {}).map(([key, value]) => [
    key,
    { main: value.main, state: value.main },
  ])
);

const noColoredShadows = Object.fromEntries(
  Object.keys(baseTheme.boxShadows?.colored || {}).map((key) => [key, "none"])
);

const heading = (size, lineHeight, weight = 600) => ({
  fontFamily: V2_DISPLAY_FONT_FAMILY,
  fontWeight: weight,
  fontSize: pxToRem(size),
  lineHeight,
  letterSpacing: "-0.01em",
  color: t.text,
  ...tabular,
});

const text = (size, lineHeight, weight = 400) => ({
  fontFamily: font,
  fontSize: pxToRem(size),
  fontWeight: weight,
  lineHeight,
});

const controlHeight = pxToRem(d.controlHeightPx);
const controlHeightSmall = pxToRem(d.controlHeightSmallPx);

// Same key as appScaleGlobals so the merge replaces the legacy zoom rather
// than leaving it in place next to ours.
const APP_SCALE_MEDIA = `@media screen and (min-width: ${APP_SCALE_MIN_WIDTH}px)`;

// Material Icons draw from ligatures ("arrow_drop_up"): a font override on them
// paints the ligature name instead of the glyph.
const ICON_FONTS = '.MuiIcon-root, [class*="material-icons"], .material-symbols-outlined';

const globalsV2 = {
  body: {
    // Read by scaledVh()/scaledVw(); legacy falls back to 0.8.
    "--app-scale": 1,
    backgroundColor: t.bg,
    color: t.text,
    fontFamily: `${font} !important`,
    fontSize: pxToRem(d.baseFontPx),
    WebkitFontSmoothing: "antialiased",
    MozOsxFontSmoothing: "grayscale",
  },
  "#app": {
    minHeight: "100vh",
    backgroundColor: t.bg,
  },
  [APP_SCALE_MEDIA]: {
    body: { zoom: 1 },
    "#app": { minHeight: "100vh" },
  },
  ":root": {
    "--pearl-background": t.bg,
    "--pearl-surface": t.surface,
    "--pearl-on-surface": t.text,
    "--pearl-on-surface-variant": t.text2,
    "--pearl-outline": t.border2,
    "--pearl-outline-variant": t.border,
  },
  // Legacy forces JetBrains Mono on every table cell; v2 sets everything in
  // Red Hat Text and keeps numbers aligned with tabular figures.
  ".MuiTableCell-body, .MuiTableCell-body .MuiTypography-root": {
    fontFamily: `${font} !important`,
    ...tabular,
  },
  // The mockups use no monospace anywhere: ids, amounts and dates that legacy
  // sets in JetBrains Mono (MonoText, variant="mono", record field values)
  // render in Red Hat Text with tabular figures under v2.
  "body.ui-v2 .font-mono, body.ui-v2 [data-font=\"mono\"]": {
    fontFamily: `${font} !important`,
    ...tabular,
  },
  // examples/Tables/DataTable (72 pages): 48px rows and Red Hat Text with
  // tabular figures, instead of the legacy forced monospace (monoDataCellSx
  // also reaches spans and links, so status badges and dates need covering).
  // The cell's inner div sets the family itself, so divs are covered too.
  // Icon fonts are left alone or their ligatures render as text.
  [`body.ui-v2 [data-table-data-cell], body.ui-v2 [data-table-data-cell] :is(div, .MuiTypography-root, span, a):not(${ICON_FONTS})`]: {
    fontFamily: `${font} !important`,
    ...tabular,
  },
  // Size only the cell and its text (as before); badges and chips keep theirs.
  "body.ui-v2 td[data-table-data-cell], body.ui-v2 [data-table-data-cell] .MuiTypography-root": {
    fontSize: pxToRem(13),
  },
  "body.ui-v2 td[data-table-data-cell]": {
    height: d.tableRowHeightPx,
    paddingTop: "10px !important",
    paddingBottom: "10px !important",
  },
  "body.ui-v2 th[data-table-head-cell]": {
    height: d.tableHeadHeightPx,
    backgroundColor: t.surface2,
    borderBottom: `1px solid ${t.border} !important`,
  },
  [`body.ui-v2 th[data-table-head-cell] *:not(${ICON_FONTS})`]: {
    fontFamily: `${font} !important`,
    fontSize: `${pxToRem(12)} !important`,
    fontWeight: "600 !important",
    letterSpacing: "0.04em",
    color: `${t.text3} !important`,
  },
  // Header strips (components/MDBox): white card headers in v2. Their
  // contents were written for a blue fill, so bare text and icons that were
  // white read dark here. Icons inside buttons are left alone: the shared
  // dark round icon buttons paint their own white icons.
  "body.ui-v2 .v2-strip": {
    color: t.text,
  },
  [`body.ui-v2 .v2-strip :is(.MuiSvgIcon-root, ${ICON_FONTS}):not(.MuiButtonBase-root *)`]: {
    color: `${t.text2} !important`,
    fontSize: `${pxToRem(18)} !important`,
  },
  // Chips and toggles drawn in translucent white on the blue fill.
  "body.ui-v2 .v2-strip .MuiChip-root": {
    backgroundColor: `${t.surface2} !important`,
    border: `1px solid ${t.border} !important`,
    color: `${t.text2} !important`,
  },
  "body.ui-v2 .v2-strip .MuiChip-root .MuiChip-label": {
    color: `${t.text2} !important`,
  },
  "body.ui-v2 .v2-strip .MuiToggleButtonGroup-root": {
    backgroundColor: "transparent !important",
  },
  "body.ui-v2 .v2-strip .MuiToggleButton-root": {
    color: `${t.text2} !important`,
    borderColor: `${t.border} !important`,
  },
  "body.ui-v2 .v2-strip .MuiToggleButton-root.Mui-selected": {
    backgroundColor: `${t.surface2} !important`,
    color: `${t.text} !important`,
  },
  // Create/edit pop-up headers (~90 modals paint colors.popupHeader blue and
  // set their title and close icon white; each header box carries
  // .pearl-popup-header). v2: a white header with dark text and a hairline.
  // Buttons inside keep their own colours.
  "body.ui-v2 .pearl-popup-header": {
    backgroundColor: `${t.surface} !important`,
    borderBottom: `1px solid ${t.border}`,
    color: t.text,
  },
  "body.ui-v2 .pearl-popup-header :is(.MuiTypography-root, h1, h2, h3, h4, h5, h6, p, span):not(.MuiButton-root *):not(.MuiChip-root *)": {
    color: `${t.text} !important`,
  },
  "body.ui-v2 .pearl-popup-header :is(h2, .MuiTypography-h6, .MuiTypography-h5):not(.MuiButton-root *)": {
    fontSize: `${pxToRem(18)} !important`,
    fontWeight: "600 !important",
  },
  [`body.ui-v2 .pearl-popup-header :is(.MuiSvgIcon-root, ${ICON_FONTS}):not(.MuiButton-root *)`]: {
    color: `${t.text2} !important`,
  },
  // A Modal focuses its content box (tabindex -1) on open; Chrome outlines it
  // in blue, which the blue header used to hide. MUI's Dialog drops it too.
  'body.ui-v2 .MuiModal-root [tabindex="-1"]:focus': {
    outline: "none",
  },
  "body.ui-v2 .pearl-popup-header .MuiIconButton-root:hover": {
    backgroundColor: t.hover,
  },
  // Echo feed on record pages (each page draws it inline in grey hex; the
  // list box carries .pearl-echo-feed). v2: hairline rows on white, the
  // author in dark 13/600 and the time and text sized as the Activity rows.
  "body.ui-v2 .pearl-echo-feed": {
    borderColor: `${t.border} !important`,
    borderRadius: "8px !important",
    backgroundColor: `${t.surface} !important`,
  },
  "body.ui-v2 .pearl-echo-feed > div": {
    borderBottomColor: `${t.border} !important`,
    backgroundColor: `${t.surface} !important`,
    "&:hover": { backgroundColor: `${t.surface2} !important` },
  },
  "body.ui-v2 .pearl-echo-feed .MuiTypography-subtitle2": {
    fontSize: `${pxToRem(13)} !important`,
    fontWeight: "600 !important",
    color: `${t.text} !important`,
  },
  "body.ui-v2 .pearl-echo-feed .MuiTypography-caption": {
    fontSize: `${pxToRem(12)} !important`,
    color: `${t.text2} !important`,
  },
  "body.ui-v2 .pearl-echo-feed .MuiTypography-body2": {
    fontSize: `${pxToRem(14)} !important`,
    color: `${t.text} !important`,
  },
  // Record pages space their side cards with <br /><br /> or empty boxes, sized for the
  // classic headers that overlap the card top by 24px. v2 headers sit inside
  // the card, so the breaks are dropped and the gap matches the main column.
  "body.ui-v2 .MuiCard-root + br, body.ui-v2 .MuiCard-root + br + br": {
    display: "none",
  },
  "body.ui-v2 .MuiCard-root + br + br + .MuiCard-root, body.ui-v2 .MuiCard-root + br + .MuiCard-root": {
    marginTop: pxToRem(20),
  },
  // ...or with empty <Box mt={5} /> spacers (40px, sometimes two in a row).
  "body.ui-v2 .MuiCard-root + .MuiBox-root:empty": {
    marginTop: `${pxToRem(20)} !important`,
  },
  "body.ui-v2 .MuiCard-root + .MuiBox-root:empty + .MuiBox-root:empty": {
    display: "none",
  },
  // Phones: table cells keep one line and the table scrolls sideways,
  // instead of breaking emails and names letter by letter.
  "@media (max-width: 600px)": {
    "body.ui-v2 .MuiTableCell-root": { whiteSpace: "nowrap" },
    // DataTable cells set break-word / anywhere on their content.
    [`body.ui-v2 [data-table-data-cell], body.ui-v2 [data-table-data-cell] :is(div, .MuiTypography-root, span, a):not(${ICON_FONTS}), body.ui-v2 th[data-table-head-cell] *`]: {
      whiteSpace: "nowrap !important",
      wordBreak: "normal !important",
      overflowWrap: "normal !important",
    },
  },
  // Tablets and narrow windows: DataTable cells may still wrap between words,
  // but never inside one ("Ravensbu / rg"). The column takes the width of
  // its longest word and the table scrolls sideways when it runs out of room.
  "@media (max-width: 1279px)": {
    [`body.ui-v2 [data-table-data-cell], body.ui-v2 [data-table-data-cell] :is(div, .MuiTypography-root, span, a):not(${ICON_FONTS})`]: {
      wordBreak: "normal !important",
      overflowWrap: "normal !important",
    },
  },
  // Below 900px a record page's main column (md 8) and side column (md 4)
  // stack, so Activities and Attachments get the full width instead of a
  // 230px strip.
  "@media (max-width: 899px)": {
    "body.ui-v2 .MuiGrid-container:has(> .MuiGrid-grid-md-8):has(> .MuiGrid-grid-md-4) > :is(.MuiGrid-grid-md-8, .MuiGrid-grid-md-4)": {
      flexBasis: "100%",
      maxWidth: "100%",
    },
  },
  // Settings sections opened in a dialog repeat the dialog title as their
  // own heading; v2 shows it once, in the dialog header.
  "body.ui-v2 .MuiDialog-paper .pearl-dialog-dup-title": {
    display: "none",
  },
  "body.ui-v2 .MuiDialog-paper .pearl-dialog-dup-title + .MuiTypography-root": {
    marginTop: pxToRem(8),
  },
  // Read-only label / value pairs drawn with a bold caption (.pearl-field-
  // label, Company Settings): grey label, dark value, as on record pages.
  "body.ui-v2 .pearl-field-label": {
    fontWeight: "400 !important",
    fontSize: `${pxToRem(12)} !important`,
    color: `${t.text2} !important`,
  },
  "body.ui-v2 .pearl-field-label + .MuiTypography-root": {
    fontSize: `${pxToRem(14)} !important`,
    color: `${t.text} !important`,
  },
  // Some selects (GeographySelect, 3 forms) pin their height inline; let the
  // control size above win so they line up with their neighbours.
  'body.ui-v2 .MuiAutocomplete-root .MuiInputBase-root[style*="height"]': {
    height: "auto !important",
  },
  // Forms in dialogs: one control height, so a size="small" select sits level
  // with the full-size field beside it, and margin="normal" no longer stacks
  // 24px on top of the grid gap.
  // (Dialogs and the many create/edit forms built on a bare Modal; menus and
  // popovers are Modal-based too but keep their compact fields.)
  "body.ui-v2 .MuiModal-root:not(.MuiPopover-root) .MuiInputBase-root.MuiInputBase-sizeSmall": {
    minHeight: controlHeight,
    fontSize: pxToRem(14),
  },
  "body.ui-v2 .MuiModal-root:not(.MuiPopover-root) .MuiInputLabel-sizeSmall.MuiInputLabel-outlined:not(.MuiInputLabel-shrink)": {
    transform: `translate(${pxToRem(12)}, ${pxToRem((d.controlHeightPx - 20) / 2)}) scale(1)`,
    fontSize: pxToRem(14),
  },
  // Some modal forms pin their fields at 45px in sx (CustomActivityModal),
  // which left the resting labels high. Single-line fields are 36px like
  // every other v2 control; multi-line fields and chip lists keep growing.
  "body.ui-v2 .MuiModal-root:not(.MuiPopover-root) .MuiOutlinedInput-root:not(.MuiInputBase-multiline):not(:has(.MuiChip-root))": {
    height: controlHeight,
  },
  "body.ui-v2 .MuiModal-root:not(.MuiPopover-root) .MuiFormControl-marginNormal": {
    marginTop: pxToRem(8),
    marginBottom: pxToRem(4),
  },
  "a.link, .link, a.link:link, .link:link, a.link:visited, .link:visited": {
    color: `${t.link} !important`,
  },
};

const themeV2 = createTheme(baseTheme, {
  // Lets shared components (MDBox, statistics cards...) tell which look is
  // rendering without subscribing to the permissions context.
  pearlUi: "v2",
  shape: { borderRadius: d.radiusPx },

  palette: {
    primary: { main: t.primary, dark: t.primaryDark, light: t.primaryLight, contrastText: t.onPrimary, focus: t.primaryDark },
    info: { main: t.primary, focus: t.primaryDark },
    background: { default: t.bg, paper: t.surface },
    text: { primary: t.text, secondary: t.text2, disabled: t.textDisabled, main: t.text, focus: t.text2 },
    divider: t.border,
    gradients: flatGradients,
  },

  typography: {
    fontFamily: font,
    fontSize: d.baseFontPx,
    h1: heading(28, 1.3),
    h2: heading(24, 1.3),
    h3: heading(20, 1.35),
    h4: heading(18, 1.4),
    h5: heading(16, 1.4),
    h6: heading(15, 1.4),
    subtitle1: text(15, 1.45, 600),
    subtitle2: text(14, 1.45, 500),
    body1: text(14, 1.45),
    body2: text(13, 1.45),
    button: { ...text(14, 1.4, 600), textTransform: "none", letterSpacing: 0 },
    caption: text(12, 1.35, 500),
    overline: { fontFamily: font },
    mono: { ...text(14, 1.43), ...tabular },
    d1: heading(48, 1.1, 700),
    d2: heading(44, 1.1, 700),
    d3: heading(40, 1.1, 700),
    d4: heading(36, 1.1, 700),
    d5: heading(32, 1.15, 700),
    d6: heading(28, 1.2, 700),
    // MDTypography `size` props. Without the 0.8 zoom the legacy scale reads
    // a step too large, so v2 pulls each step down.
    size: {
      xxs: pxToRem(10.5),
      xs: pxToRem(12),
      sm: pxToRem(13),
      md: pxToRem(14),
      lg: pxToRem(16),
      xl: pxToRem(18),
      "2xl": pxToRem(20),
      "3xl": pxToRem(24),
    },
    families: { display: font, body: font, mono: font },
  },

  borders: {
    borderColor: t.border,
    borderRadius: {
      sm: pxToRem(6),
      md: pxToRem(8),
      lg: pxToRem(10),
      xl: pxToRem(12),
      xxl: pxToRem(12),
      section: pxToRem(8),
    },
  },

  // MUI elevations (sx boxShadow: 3, Paper elevation). The mockups group a
  // record's sections flat inside one card, so the low elevations that legacy
  // pages use to float sections are dropped; menus and dialogs keep theirs.
  shadows: Array.from({ length: 25 }, (_, i) => (i <= 4 ? "none" : i <= 12 ? t.shadowMenu : t.shadowPop)),

  boxShadows: {
    xs: t.shadow,
    sm: t.shadow,
    md: t.shadow,
    lg: t.shadowMenu,
    xl: t.shadowPop,
    xxl: t.shadowPop,
    colored: noColoredShadows,
  },

  components: {
    MuiCssBaseline: { styleOverrides: globalsV2 },

    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          fontFamily: font,
          fontSize: pxToRem(14),
          borderRadius: d.radiusPx,
          minHeight: controlHeight,
          padding: "0 14px",
          boxShadow: "none",
          textTransform: "none",
          fontWeight: 600,
        },
        contained: {
          minHeight: controlHeight,
          padding: "0 14px",
          backgroundImage: "none",
          "&:hover": { boxShadow: "none" },
          // Readable disabled state: classic leaves white text on a faded fill.
          "&.Mui-disabled": { backgroundColor: t.hover, color: t.text3 },
        },
        containedSizeSmall: { minHeight: controlHeightSmall, padding: "0 10px", fontSize: pxToRem(13) },
        containedSizeLarge: { minHeight: pxToRem(40), padding: "0 18px", fontSize: pxToRem(14) },
        outlined: { minHeight: controlHeight, padding: "0 14px", borderColor: t.border2, color: t.text, backgroundColor: t.surface },
        outlinedSizeSmall: { minHeight: controlHeightSmall, padding: "0 10px", fontSize: pxToRem(13) },
        outlinedSizeLarge: { minHeight: pxToRem(40), padding: "0 18px", fontSize: pxToRem(14) },
        text: { minHeight: controlHeight, padding: "0 10px" },
        textSizeSmall: { minHeight: controlHeightSmall, padding: "0 8px", fontSize: pxToRem(13) },
        textSizeLarge: { minHeight: pxToRem(40), fontSize: pxToRem(14) },
      },
    },
    MuiIconButton: { styleOverrides: { root: { borderRadius: d.radiusPx } } },
    // Classic forces fontSize "inherit !important" on every icon, which
    // overrides the size a component asks for (fontSize: 40 rendered at the
    // 12px of the text around it). v2 keeps inherit as the default only.
    MuiSvgIcon: { styleOverrides: { fontSizeInherit: { fontSize: "inherit" } } },

    MuiCard: {
      styleOverrides: {
        root: { borderRadius: d.cardRadiusPx, border: `1px solid ${t.border}`, boxShadow: "none", backgroundImage: "none" },
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: { borderRadius: d.cardRadiusPx },
        elevation1: { boxShadow: t.shadow },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: { fontFamily: font, borderRadius: d.radiusPx, backgroundColor: t.surface, fontSize: pxToRem(14), minHeight: controlHeight },
        notchedOutline: { borderColor: t.border2 },
        input: { padding: "0 12px" },
        multiline: { padding: "8px 12px" },
        sizeSmall: { minHeight: controlHeightSmall, borderRadius: 6, fontSize: pxToRem(13) },
        inputSizeSmall: { padding: "0 10px", fontSize: pxToRem(13) },
      },
    },
    // The classic label rests at the centre of a 48px / 40px control; v2
    // controls are 36px / 30px, so the resting label is re-centred to match.
    MuiInputLabel: {
      styleOverrides: {
        root: { fontFamily: font, fontSize: pxToRem(14), color: t.text2, "&.Mui-focused": { color: t.primary } },
        outlined: {
          "&:not(.MuiInputLabel-shrink)": {
            transform: `translate(${pxToRem(12)}, ${pxToRem((d.controlHeightPx - 20) / 2)}) scale(1)`,
          },
          "&.MuiInputLabel-shrink": { transform: `translate(${pxToRem(12)}, ${pxToRem(-9)}) scale(0.75)` },
        },
        sizeSmall: {
          "&.MuiInputLabel-outlined:not(.MuiInputLabel-shrink)": {
            transform: `translate(${pxToRem(10)}, ${pxToRem((d.controlHeightSmallPx - 18) / 2)}) scale(1)`,
          },
          "&.MuiInputLabel-shrink": { transform: `translate(${pxToRem(10)}, ${pxToRem(-9)}) scale(0.75)` },
        },
      },
    },

    // Autocompletes are the same 36px / 30px controls as the other inputs
    // (classic sizes them 48px / 40px).
    MuiAutocomplete: {
      styleOverrides: {
        // Same nested keys as classic's root rule, so the merge replaces them.
        root: {
          "& .MuiOutlinedInput-root": { minHeight: controlHeight, paddingTop: 0, paddingBottom: 0 },
          "& .MuiOutlinedInput-root.MuiInputBase-sizeSmall": { minHeight: controlHeightSmall, paddingTop: 0, paddingBottom: 0 },
        },
        inputRoot: {
          minHeight: controlHeight,
          paddingTop: 0,
          paddingBottom: 0,
          "&.MuiInputBase-sizeSmall": { minHeight: controlHeightSmall, paddingTop: 0, paddingBottom: 0 },
        },
      },
    },

    // Initials avatars with no colour of their own (Teams, member lists): MUI's
    // default is near-white on pale grey; v2 uses the info tint and its ink.
    MuiAvatar: {
      styleOverrides: {
        colorDefault: { backgroundColor: v2Tones.info.bg, color: v2Tones.info.fg, fontWeight: 600 },
      },
    },

    MuiDialog: { styleOverrides: { paper: { borderRadius: 12, boxShadow: t.shadowPop } } },
    MuiDialogTitle: { styleOverrides: { root: { fontFamily: font, fontWeight: 600, fontSize: pxToRem(18), color: t.text } } },
    MuiMenu: { styleOverrides: { paper: { borderRadius: d.cardRadiusPx, border: `1px solid ${t.border}`, boxShadow: t.shadowMenu } } },
    MuiMenuItem: { styleOverrides: { root: { fontFamily: font, fontSize: pxToRem(14), minHeight: 36 } } },
    // Solid surface: the classic theme makes popover paper transparent.
    MuiPopover: { styleOverrides: { paper: { borderRadius: d.cardRadiusPx, boxShadow: t.shadowMenu, backgroundColor: t.surface } } },

    MuiChip: {
      styleOverrides: {
        root: { fontFamily: font, borderRadius: d.cardRadiusPx, height: 24, fontSize: pxToRem(12), fontWeight: 600 },
        sizeSmall: { height: 22, fontSize: pxToRem(12) },
        label: { paddingLeft: 8, paddingRight: 8 },
      },
    },

    MuiTabs: {
      styleOverrides: {
        root: { minHeight: 44, borderBottom: `1px solid ${t.border}` },
        indicator: { height: 2, borderRadius: 0 },
      },
    },
    MuiTab: { styleOverrides: { root: { fontFamily: font, minHeight: 44, textTransform: "none", fontWeight: 500, fontSize: pxToRem(14) } } },

    MuiTableRow: {
      styleOverrides: {
        root: { height: d.tableRowHeightPx },
        head: { height: d.tableHeadHeightPx },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontFamily: font,
          fontSize: pxToRem(12),
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          color: t.text3,
          backgroundColor: t.surface2,
        },
        body: {
          fontFamily: font,
          fontSize: pxToRem(13),
          color: t.text,
          borderBottom: `1px solid ${t.border}`,
          ...tabular,
          // Same selector as classic's nested mono rule, so the merge replaces it.
          "& .MuiTypography-root, & span, & a": { fontFamily: `${font} !important`, ...tabular },
        },
      },
    },

    MuiTooltip: { styleOverrides: { tooltip: { fontFamily: font, borderRadius: 6, backgroundColor: t.header, fontSize: pxToRem(12) } } },
    MuiBreadcrumbs: { styleOverrides: { root: { fontFamily: font, fontSize: pxToRem(13) } } },
  },
});

export default themeV2;
